/**
 * The application frame.
 *
 * Three navigation presentations, chosen by width (plan §7.3):
 *
 *   - **≥1024px** a persistent navy left rail.
 *   - **<1024px** a bottom tab bar for the primary destinations, plus a drawer
 *     holding everything.
 *
 * The degradation preserves function rather than shrinking the rail into
 * something unusable: at 375px a 236px rail would leave no room for content,
 * and PRD §11 expects the pipeline to be usable from a phone.
 *
 * Every target is at least 44px, which is the smallest reliable touch target.
 *
 * **The rail is navy** (task 6.5.5). That is not decoration: the design system
 * mandates a WHITE 55 / NAVY 25 / BLUE 12 / GREEN 8 split, and the rail is
 * where navy's quarter of the interface lives. Built white — as Phase 5 built
 * it — the product is token-clean and still off-brand, because a token check
 * can see an invented colour but not a missing one (§4.5).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 5.7, 6.5.5, 6.5.6, §7.3, §4.5
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router';

import { useAuth } from '../../lib/auth-context';
import { hasRole } from '../../lib/auth-types';
import { cn } from '../../lib/cn';
import { GvLogo } from '../ui/GvLogo';
import { Icon } from '../ui/Icon';

import { groupNavItems, type NavCounts } from './nav-grouping';
import { NAV_GROUPS, NAV_ITEMS, type NavItem } from './nav-items';
import { TopBar } from './TopBar';

/**
 * A navigation link, styled for whichever presentation contains it.
 *
 * The rail and drawer variants sit on navy and the bar variant sits on white,
 * so they cannot share a colour scheme — which is why the variant selects the
 * whole palette rather than only the layout.
 */
function NavItemLink({
  item,
  variant,
  count,
  onNavigate,
}: {
  item: NavItem;
  variant: 'rail' | 'bar';
  count?: number | undefined;
  onNavigate?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      /* Named explicitly when a count is shown, so a screen reader hears
         "Startups, 47 records" rather than the label and a bare figure run
         together. Without a count the visible text is already the name. */
      {...(variant === 'rail' && count !== undefined
        ? { 'aria-label': `${t(item.labelKey)}, ${t('nav.recordCount', { count })}` }
        : {})}
      className={({ isActive }) =>
        cn(
          'flex items-center transition-colors',
          variant === 'rail'
            ? /* A 3px leading edge on every item, transparent until active, so
                 the active state does not shift the label sideways by 3px. */
              'min-h-11 justify-between gap-2 border-l-[3px] px-s4 py-2.5 text-[13.5px]'
            : 'min-h-11 flex-1 flex-col justify-center gap-1 px-2 py-1 text-[10px]',
          variant === 'rail' &&
            (isActive
              ? 'border-l-green bg-white/[0.09] font-semibold text-white'
              : 'border-l-transparent text-gray-200 hover:bg-white/[0.06] hover:text-white'),
          variant === 'bar' &&
            (isActive
              ? 'bg-blue-light text-blue-strong font-semibold'
              : 'text-body hover:bg-gray-100'),
        )
      }
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <Icon name={item.icon} size="sm" />
        <span className="truncate">{t(item.labelKey)}</span>
      </span>
      {variant === 'rail' && count !== undefined && (
        /* Hidden from the accessibility tree, because the anchor's own
           `aria-label` already says the count in words. Left visible, the
           figure is concatenated onto the label with no separator —
           "Startups47", announced as one token — and neither a whitespace text
           node nor a non-breaking space survives name computation. */
        <span
          aria-hidden="true"
          className="font-display text-green flex-none text-[11.5px] font-extrabold tabular-nums"
        >
          {count}
        </span>
      )}
    </NavLink>
  );
}

/**
 * The rail's contents — logo, grouped links, foot note.
 *
 * Shared by the persistent rail and the mobile drawer, which are the same
 * navigation at different widths. Rendering it twice from one function is what
 * stops the drawer quietly falling behind the rail.
 */
function NavBody({
  items,
  counts,
  onNavigate,
}: {
  items: readonly NavItem[];
  counts: NavCounts;
  onNavigate?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const groups = groupNavItems(items);

  return (
    <>
      <div className="px-s4 pb-s4">
        {/* Clear space off: the rail's own `px-s4` already provides it, and
            applying both would push the mark visibly off the grid. */}
        <GvLogo on="navy" height={32} clearSpace={false} />
      </div>

      {NAV_GROUPS.filter((group) => groups[group].length > 0).map((group) => (
        <div key={group}>
          <h2 className="font-display px-s4 pt-s4 pb-s2 text-[10px] font-extrabold tracking-[0.2em] text-gray-200 uppercase">
            {t(`nav.group.${group}`)}
          </h2>
          <ul>
            {groups[group].map((item) => (
              <li key={item.path}>
                <NavItemLink
                  item={item}
                  variant="rail"
                  count={item.countKey === undefined ? undefined : counts[item.countKey]}
                  {...(onNavigate === undefined ? {} : { onNavigate })}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}

      <p className="mx-s4 mt-s4 border-t border-white/[0.12] pt-s3 text-[11px] leading-relaxed font-light text-gray-200">
        {t('app.name')} · {t('app.tagline')}
      </p>
    </>
  );
}

export interface AppShellProps {
  /**
   * Record counts shown beside the rail's links.
   *
   * Defaulted to empty rather than required: the shell renders before any list
   * query resolves, and a rail that waited for counts would leave the user
   * looking at nothing. An absent count renders no figure at all, which reads
   * as "not known yet" rather than as zero.
   */
  counts?: NavCounts;
}

export function AppShell({ counts = {} }: AppShellProps): React.JSX.Element {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* Filtered by role so the navigation never offers a destination that would
     redirect straight back — the server decides, but the menu should not lie. */
  const visible = NAV_ITEMS.filter((item) => hasRole(user?.role, item.role));
  const primary = visible.filter((item) => item.primary);

  const closeDrawer = (): void => {
    setDrawerOpen(false);
  };

  return (
    <div className="min-h-dvh lg:flex">
      {/* Persistent rail, wide screens only. */}
      <nav
        aria-label={t('nav.primary')}
        className="bg-navy w-nav py-s4 sticky top-0 hidden h-dvh flex-none overflow-y-auto lg:block"
      >
        <NavBody items={visible} counts={counts} />
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          user={user}
          onOpenMenu={() => {
            setDrawerOpen(true);
          }}
          menuExpanded={drawerOpen}
          onSignOut={() => void signOut()}
        />

        {/* `pb-20` on small screens keeps content clear of the bottom bar. */}
        <main className="p-s4 pb-20 min-w-0 flex-1 lg:pb-s4">
          <div className="mx-auto w-full max-w-[1520px]">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom bar: the primary destinations, on small screens only. */}
      <nav
        aria-label={t('nav.sections')}
        className="border-gray-100 fixed inset-x-0 bottom-0 flex border-t bg-white px-2 py-1 lg:hidden"
      >
        {primary.map((item) => (
          <NavItemLink key={item.path} item={item} variant="bar" />
        ))}
      </nav>

      {/* Drawer: everything, including what the bottom bar cannot hold. */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label={t('nav.closeMenu')}
            onClick={closeDrawer}
            className="bg-navy/40 absolute inset-0"
          />
          <nav
            aria-label={t('nav.menu')}
            className="bg-navy w-nav py-s4 absolute inset-y-0 left-0 overflow-y-auto shadow-md"
          >
            <NavBody items={visible} counts={counts} onNavigate={closeDrawer} />
          </nav>
        </div>
      ) : null}
    </div>
  );
}
